import { z } from 'zod';
import { CountryCodeSchema, type CountryCode } from './geo';

/* ------------------------------------------------------------------ */
/* Config registry (W3)                                                */
/*                                                                     */
/* Every admin-set business number (fees, timers, radii, penalties,    */
/* caps) is one typed key. A value can be set globally, per country,   */
/* per town or per zone; the most specific one wins:                   */
/*   zone → town → country → global.                                   */
/* Money is always stored in minor units (paise / paisa) and can only  */
/* be set per country or narrower, because currencies differ.          */
/* ------------------------------------------------------------------ */

export const CONFIG_SCOPE_TYPES = ['global', 'country', 'town', 'zone'] as const;
export const ConfigScopeTypeSchema = z.enum(CONFIG_SCOPE_TYPES);
export type ConfigScopeType = z.infer<typeof ConfigScopeTypeSchema>;

export const CONFIG_VALUE_TYPES = [
  'integer',
  'decimal',
  'money',
  'percent',
  'boolean',
  'enum',
  'time',
  /** Short free text (W21: grievance officer details). Up to 200 characters. */
  'text',
] as const;
export const ConfigValueTypeSchema = z.enum(CONFIG_VALUE_TYPES);
export type ConfigValueType = z.infer<typeof ConfigValueTypeSchema>;

export const ConfigKeySchema = z
  .string()
  .min(3)
  .max(80)
  .regex(/^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/, 'Invalid config key');

export const ConfigValueSchema = z.union([z.number(), z.boolean(), z.string().max(200)]);
export type ConfigValue = z.infer<typeof ConfigValueSchema>;

export const CONFIG_GROUPS = {
  orders: 'Orders & cancellations',
  fees: 'Customer fees',
  delivery: 'Delivery radius & hours',
  dispatch: 'Dispatch & rider matching',
  restaurants: 'Restaurant operations',
  onboarding: 'Restaurant onboarding',
  menu: 'Menus',
  riders: 'Riders',
  cod: 'Cash on delivery',
  trust: 'Trust & safety',
  compensation: 'Compensation & credits',
  eta: 'Delivery time estimates',
  tracking: 'Live tracking & delivery proof',
  growth: 'Growth & marketing',
  ads: 'Ads',
  subscriptions: 'Partner plans & billing',
  payouts: 'Payouts',
  payments: 'Payments & Capsico Money',
  tax: 'Tax',
  discovery: 'Search & discovery',
  tower: 'Control tower',
  support: 'Support & refunds',
  mi: 'Market Initialers',
  legal: 'Legal & grievance',
  launch: 'Launch gates',
  security: 'Security & app integrity',
} as const;
export type ConfigGroup = keyof typeof CONFIG_GROUPS;
export const ConfigGroupSchema = z.enum(
  Object.keys(CONFIG_GROUPS) as [ConfigGroup, ...ConfigGroup[]],
);

/** A key definition. Kept in code, synced into config_keys by the seed script. */
export interface ConfigKeyDef {
  key: string;
  group: ConfigGroup;
  label: string;
  description: string;
  type: ConfigValueType;
  /** Shown after the number: "min", "km", "%" … Money units come from the country. */
  unit?: string;
  min?: number;
  max?: number;
  options?: readonly string[];
  scopes: readonly ConfigScopeType[];
  /** Starting values from the discovery decisions (money in minor units). */
  seed: { global?: ConfigValue; IN?: ConfigValue; NP?: ConfigValue };
  /** Set when the Nepal seed value was converted from the India amount and still needs a decision. */
  nepalEstimated?: boolean;
}

const ALL: readonly ConfigScopeType[] = ['global', 'country', 'town', 'zone'];
const COUNTRY_DOWN: readonly ConfigScopeType[] = ['country', 'town', 'zone'];
const COUNTRY_ONLY: readonly ConfigScopeType[] = ['country'];
const GLOBAL_COUNTRY: readonly ConfigScopeType[] = ['global', 'country'];

/** Rupees → minor units (paise / paisa). */
const rs = (amount: number) => Math.round(amount * 100);
/** Minutes after midnight. */
const at = (hours: number, minutes = 0) => hours * 60 + minutes;

const minutes = (
  key: string,
  group: ConfigGroup,
  label: string,
  description: string,
  value: number,
  max = 240,
  scopes: readonly ConfigScopeType[] = ALL,
): ConfigKeyDef => ({
  key,
  group,
  label,
  description,
  type: 'integer',
  unit: 'min',
  min: 0,
  max,
  scopes,
  seed: { global: value },
});

const money = (
  key: string,
  group: ConfigGroup,
  label: string,
  description: string,
  india: number,
  nepal: number,
  nepalEstimated = false,
  max = rs(100_000),
): ConfigKeyDef => ({
  key,
  group,
  label,
  description,
  type: 'money',
  min: 0,
  max,
  scopes: COUNTRY_DOWN,
  seed: { IN: rs(india), NP: rs(nepal) },
  ...(nepalEstimated ? { nepalEstimated: true } : {}),
});

const count = (
  key: string,
  group: ConfigGroup,
  label: string,
  description: string,
  value: number,
  unit: string,
  max = 1000,
  scopes: readonly ConfigScopeType[] = ALL,
): ConfigKeyDef => ({
  key,
  group,
  label,
  description,
  type: 'integer',
  unit,
  min: 0,
  max,
  scopes,
  seed: { global: value },
});

const percent = (
  key: string,
  group: ConfigGroup,
  label: string,
  description: string,
  value: number,
  scopes: readonly ConfigScopeType[] = ALL,
): ConfigKeyDef => ({
  key,
  group,
  label,
  description,
  type: 'percent',
  unit: '%',
  min: 0,
  max: 100,
  scopes,
  seed: { global: value },
});

const km = (
  key: string,
  group: ConfigGroup,
  label: string,
  description: string,
  value: number,
): ConfigKeyDef => ({
  key,
  group,
  label,
  description,
  type: 'decimal',
  unit: 'km',
  min: 0,
  max: 30,
  scopes: ALL,
  seed: { global: value },
});

const clock = (
  key: string,
  group: ConfigGroup,
  label: string,
  description: string,
  value: number,
): ConfigKeyDef => ({
  key,
  group,
  label,
  description,
  type: 'time',
  min: 0,
  max: 1439,
  scopes: ALL,
  seed: { global: value },
});

/** Short free text set per country (W21 grievance officer). */
const text = (
  key: string,
  group: ConfigGroup,
  label: string,
  description: string,
  india: string,
  nepal: string,
  max = 200,
): ConfigKeyDef => ({
  key,
  group,
  label,
  description,
  type: 'text',
  min: 1,
  max,
  scopes: COUNTRY_ONLY,
  seed: { IN: india, NP: nepal },
});

/**
 * The catalogue. Seed values are the numbers decided in discovery (Oct 2026). Where only an India
 * amount was decided, the Nepal amount is converted (~1.6×, rounded) and flagged as estimated.
 */
export const CONFIG_CATALOGUE: readonly ConfigKeyDef[] = [
  /* Orders & cancellations */
  count(
    'orders.free_cancel_seconds',
    'orders',
    'Free cancellation window',
    'Customers can cancel for free this long after placing an order.',
    60,
    's',
    600,
  ),
  minutes(
    'orders.complaint_window_minutes',
    'orders',
    'Complaint window after delivery',
    'How long after delivery a customer can report a damaged or missing item with a photo.',
    30,
    1440,
  ),
  minutes(
    'orders.scheduled_release_minutes',
    'orders',
    'Scheduled orders reach the kitchen',
    'Minutes before the delivery time that a scheduled order rings in the Partner app.',
    45,
    240,
  ),
  {
    key: 'orders.scheduled_prepaid_only',
    group: 'orders',
    label: 'Scheduled orders must be prepaid',
    description: 'When on, scheduled orders cannot use cash on delivery.',
    type: 'boolean',
    scopes: ALL,
    seed: { global: true },
  },
  money(
    'orders.delivery_code_above',
    'orders',
    'Delivery code above order value',
    'Orders worth more than this need the 4-digit delivery code at the door.',
    500,
    750,
  ),
  minutes(
    'orders.unreachable_write_off_minutes',
    'orders',
    'Unreachable customer — give up after',
    'Minutes the rider waits at the address (with logged calls) before the order is written off.',
    10,
    60,
  ),
  count(
    'orders.unreachable_call_attempts',
    'orders',
    'Unreachable customer — calls before giving up',
    'Masked calls the rider must log before the order can be written off.',
    2,
    'calls',
    10,
  ),

  /* Customer fees */
  percent(
    'fees.max_customer_fees_percent',
    'fees',
    'Customer fees cap (% of food)',
    'All customer fees together may not exceed this share of the food value.',
    20,
  ),
  money(
    'fees.max_customer_fees',
    'fees',
    'Customer fees cap (amount)',
    'All customer fees together may not exceed this amount per order.',
    40,
    65,
    true,
  ),
  money(
    'fees.packaging_max_per_item',
    'fees',
    'Packaging charge cap per dish',
    'Highest packaging charge a restaurant may add per dish.',
    10,
    15,
    true,
  ),
  money(
    'fees.packaging_max_per_order',
    'fees',
    'Packaging charge cap per order',
    'Highest packaging charge a restaurant may add per order.',
    30,
    50,
    true,
  ),
  money(
    'fees.rain_fee_min',
    'fees',
    'Rain fee — lowest',
    'Lowest rain fee charged when a town or zone is in rain mode.',
    15,
    25,
    true,
  ),
  money(
    'fees.rain_fee_max',
    'fees',
    'Rain fee — highest',
    'Highest rain fee charged when a town or zone is in rain mode.',
    25,
    40,
    true,
  ),
  money(
    'fees.festival_fee_min',
    'fees',
    'Festival fee — lowest',
    'Lowest fee during festival mode.',
    20,
    30,
    true,
  ),
  money(
    'fees.festival_fee_max',
    'fees',
    'Festival fee — highest',
    'Highest fee during festival mode.',
    30,
    50,
    true,
  ),
  percent(
    'fees.rain_fee_rider_share_percent',
    'fees',
    'Rain fee share paid to the rider',
    'Part of the rain fee passed to the rider; Capsico keeps the rest.',
    80,
  ),

  /* Delivery radius & hours */
  km(
    'delivery.radius_max_km',
    'delivery',
    'Delivery radius — normal',
    'Furthest a customer can be from the restaurant at normal times.',
    7,
  ),
  km(
    'delivery.radius_peak_km',
    'delivery',
    'Delivery radius — peak',
    'Radius used when the zone is busy.',
    4,
  ),
  km(
    'delivery.radius_night_km',
    'delivery',
    'Delivery radius — late night',
    'Radius used after the late-night start time.',
    4,
  ),
  km(
    'delivery.radius_rain_km',
    'delivery',
    'Delivery radius — rain',
    'Radius used in rain mode.',
    2,
  ),
  clock(
    'delivery.night_starts_at',
    'delivery',
    'Late night starts at',
    'From this time the late-night radius, buffers and dark theme apply.',
    at(22),
  ),
  km(
    'delivery.walker_max_km',
    'delivery',
    'Walker delivery distance',
    'Longest trip offered to walking riders.',
    1,
  ),
  km(
    'delivery.cyclist_max_km',
    'delivery',
    'Cyclist delivery distance',
    'Longest trip offered to riders on bicycles.',
    2,
  ),
  km(
    'delivery.campus_pass_free_km',
    'delivery',
    'Campus Pass free-delivery distance',
    'Campus Pass orders within this distance have no delivery fee.',
    1.5,
  ),
  minutes(
    'delivery.handover_minutes',
    'delivery',
    'Handover time at the door',
    'Minutes added to the estimate for handing over the order.',
    2,
    30,
  ),
  minutes(
    'delivery.handover_campus_minutes',
    'delivery',
    'Handover time on campuses',
    'Handover minutes for campuses and gated areas.',
    4,
    30,
  ),

  /* Dispatch & rider matching */
  count(
    'dispatch.batch_interval_seconds',
    'dispatch',
    'Matching round every',
    'How often orders and riders in a zone are matched.',
    20,
    's',
    300,
  ),
  count(
    'dispatch.offer_timeout_seconds',
    'dispatch',
    'Offer timeout',
    'Seconds a rider has to accept an offer before it moves to the next rider.',
    30,
    's',
    300,
  ),
  count(
    'dispatch.max_stacked_orders',
    'dispatch',
    'Orders per trip',
    'Most orders one rider may carry on the same trip.',
    2,
    'orders',
    5,
  ),
  minutes(
    'dispatch.stack_max_extra_minutes',
    'dispatch',
    'Stacking — extra time allowed',
    'A second order may not add more than this to the first customer’s wait.',
    12,
    60,
  ),
  count(
    'dispatch.stack_max_restaurant_gap_m',
    'dispatch',
    'Stacking — restaurants within',
    'Two restaurants can share a trip only if they are this close.',
    300,
    'm',
    5000,
  ),
  count(
    'dispatch.decline_window_offers',
    'dispatch',
    'Declines counted over',
    'Declines are judged over this many recent offers.',
    10,
    'offers',
    100,
  ),
  count(
    'dispatch.decline_cooloff_after',
    'dispatch',
    'Cool-off after declines',
    'This many declines in the window pauses the rider.',
    3,
    'declines',
    20,
  ),
  minutes(
    'dispatch.decline_cooloff_minutes',
    'dispatch',
    'Cool-off length',
    'How long a rider is paused after too many declines.',
    15,
    240,
  ),
  minutes(
    'dispatch.no_rider_bonus_after_minutes',
    'dispatch',
    'No rider — add bonus after',
    'Minutes without a rider before a pickup bonus is added.',
    4,
    60,
  ),
  money(
    'dispatch.no_rider_bonus',
    'dispatch',
    'No rider — pickup bonus',
    'Bonus added to the trip (paid by Capsico) when no rider has accepted.',
    10,
    15,
  ),
  minutes(
    'dispatch.no_rider_widen_after_minutes',
    'dispatch',
    'No rider — widen search after',
    'Minutes before the rider search area is widened.',
    6,
    60,
  ),
  km(
    'dispatch.no_rider_widen_km',
    'dispatch',
    'No rider — widen search by',
    'Extra distance added to the rider search.',
    2,
  ),
  minutes(
    'dispatch.no_rider_ops_alert_after_minutes',
    'dispatch',
    'No rider — alert ops after',
    'Minutes before the town fleet captain is alerted.',
    8,
    60,
  ),
  minutes(
    'dispatch.no_rider_cancel_after_minutes',
    'dispatch',
    'No rider — cancel after',
    'Minutes before the order is cancelled and refunded.',
    10,
    120,
  ),
  minutes(
    'dispatch.rider_late_warn_minutes',
    'dispatch',
    'Rider late to restaurant — warn after',
    'Minutes past the expected arrival before the rider is warned.',
    5,
    60,
  ),
  minutes(
    'dispatch.rider_late_reassign_minutes',
    'dispatch',
    'Rider late to restaurant — reassign after',
    'Minutes past the expected arrival before the order goes to another rider.',
    8,
    60,
  ),
  minutes(
    'dispatch.food_wait_remake_minutes',
    'dispatch',
    'Food waiting — remake after',
    'If ready food waits this long for pickup it is remade.',
    20,
    120,
  ),
  count(
    'dispatch.rider_gps_interval_seconds',
    'dispatch',
    'Rider location update every',
    'How often a rider on an order sends their location.',
    5,
    's',
    120,
  ),
  km(
    'dispatch.search_radius_km',
    'dispatch',
    'Rider search radius',
    'Riders farther than this (straight line) from the restaurant are not offered the order.',
    3,
  ),
  minutes(
    'dispatch.default_approach_minutes',
    'dispatch',
    'Rider approach time (fallback)',
    'Used to time the search when no rider location is known: the search starts this long (plus 1 min) before the food is ready.',
    6,
    60,
  ),
  {
    key: 'dispatch.road_factor',
    group: 'dispatch',
    label: 'Rider road distance factor',
    description:
      'Straight-line distance × this ≈ road distance for rider approach and stacking, until road routing arrives.',
    type: 'decimal',
    unit: '×',
    min: 1,
    max: 3,
    scopes: ALL,
    seed: { global: 1.3 },
  },
  count(
    'dispatch.own_fleet_first_seconds',
    'dispatch',
    'Restaurant own riders first for',
    'A restaurant’s own riders get its orders alone for this long before Capsico riders join.',
    60,
    's',
    600,
  ),
  money(
    'dispatch.no_rider_credit',
    'dispatch',
    'No rider — customer credit',
    'Capsico Money given to the customer when an order is cancelled because no rider was found.',
    50,
    75,
  ),
  count(
    'dispatch.no_rider_credit_days',
    'dispatch',
    'No rider — credit valid for',
    'Days the no-rider credit can be used.',
    7,
    'days',
    90,
  ),

  /* Restaurant operations */
  minutes(
    'restaurants.accept_ring_minutes',
    'restaurants',
    'New order — loud ring for',
    'Minutes the Partner app rings before the next alert step.',
    2,
    30,
  ),
  minutes(
    'restaurants.accept_ivr_after_minutes',
    'restaurants',
    'New order — phone call after',
    'Minutes before an automatic phone call asks the restaurant to accept.',
    5,
    30,
  ),
  minutes(
    'restaurants.accept_auto_cancel_minutes',
    'restaurants',
    'New order — cancel if not accepted after',
    'Minutes before an unaccepted order is cancelled and refunded.',
    8,
    60,
  ),
  money(
    'restaurants.missed_order_fee',
    'restaurants',
    'Missed order fee',
    'Charged to the restaurant when an order is cancelled because it was not accepted.',
    50,
    50,
  ),
  money(
    'restaurants.reject_penalty',
    'restaurants',
    'Rejection penalty',
    'Charged when a restaurant rejects an order without a valid reason.',
    40,
    65,
    true,
  ),
  percent(
    'restaurants.reject_rate_limit_percent',
    'restaurants',
    'Weekly rejection limit',
    'Above this weekly rejection rate the restaurant drops in ranking.',
    3,
  ),
  percent(
    'restaurants.reject_rank_penalty_percent',
    'restaurants',
    'Ranking drop for high rejections',
    'How far the restaurant drops in ranking when over the rejection limit.',
    20,
  ),
  minutes(
    'restaurants.prep_minutes_per_active_order',
    'restaurants',
    'Extra prep time per live order',
    'Added to the prep estimate for each order the kitchen is already cooking.',
    2,
    30,
  ),
  {
    key: 'restaurants.auto_accept_default',
    group: 'restaurants',
    label: 'New outlets auto-accept orders',
    description:
      'When on, a new outlet starts in auto-accept: orders go straight to Preparing. Owners can switch to manual.',
    type: 'boolean',
    scopes: ALL,
    seed: { global: true },
  },
  minutes(
    'restaurants.default_prep_minutes',
    'restaurants',
    'Prep time when a dish has none',
    'Used for the prep estimate when a dish on the order has no cooking time set.',
    15,
    120,
  ),
  {
    key: 'restaurants.seal_required',
    group: 'restaurants',
    label: 'Tamper seal before “Food ready”',
    description:
      'When on, the kitchen scans the serial-numbered seal tape (or says why it is missing) before marking food ready.',
    type: 'boolean',
    scopes: ALL,
    seed: { global: true },
  },
  minutes(
    'restaurants.kitchen_busy_step_minutes',
    'restaurants',
    'Kitchen busy step',
    '“Kitchen busy” adds this many minutes per tap (one or two taps).',
    10,
    60,
  ),
  clock(
    'restaurants.sold_out_reset_at',
    'restaurants',
    'Sold-out-for-today resets at',
    'Items marked sold out for today come back at this time.',
    at(4),
  ),
  count(
    'restaurants.max_pause_hours',
    'restaurants',
    'Longest restaurant pause',
    'Ops can pause a restaurant for at most this many hours at a time.',
    4,
    'h',
    72,
  ),
  money(
    'restaurants.price_mismatch_fee',
    'restaurants',
    'Price mismatch fee',
    'Charged when app prices are confirmed higher than the menu card.',
    500,
    750,
  ),
  count(
    'restaurants.new_listing_days',
    'restaurants',
    '“Newly opened” shown for',
    'Days a new restaurant appears in the Newly opened row.',
    14,
    'days',
    90,
  ),
  {
    key: 'restaurants.new_listing_boost',
    group: 'restaurants',
    label: 'Newly opened ranking boost',
    description: 'Ranking multiplier for newly opened restaurants (1 = no boost).',
    type: 'decimal',
    unit: '×',
    min: 1,
    max: 3,
    scopes: ALL,
    seed: { global: 1.35 },
  },
  count(
    'restaurants.launch_free_orders',
    'restaurants',
    'Free launch phase — orders',
    'New restaurants pay nothing until this many orders (or the days below, whichever first).',
    30,
    'orders',
    1000,
  ),
  count(
    'restaurants.launch_free_days',
    'restaurants',
    'Free launch phase — days',
    'New restaurants pay nothing for this many days (or the orders above, whichever first).',
    30,
    'days',
    365,
  ),
  count(
    'restaurants.hygiene_green_min',
    'restaurants',
    'Hygiene badge — green from',
    'Hygiene score needed for the green badge.',
    90,
    'points',
    100,
  ),
  count(
    'restaurants.hygiene_amber_min',
    'restaurants',
    'Hygiene badge — amber from',
    'Hygiene score needed for the amber badge; below it no badge is shown.',
    75,
    'points',
    100,
  ),

  /* Restaurant onboarding (W4) */
  count(
    'onboarding.review_sla_hours',
    'onboarding',
    'Ops review within',
    'Hours Ops has to review a submitted restaurant. Late reviews show as overdue.',
    12,
    'h',
    168,
  ),
  count(
    'onboarding.kitchen_photos_min',
    'onboarding',
    'Kitchen photos needed',
    'Geotagged kitchen photos needed before a restaurant can be submitted.',
    3,
    'photos',
    6,
  ),
  count(
    'onboarding.photo_max_distance_m',
    'onboarding',
    'Photo distance from the outlet',
    'Photos taken further than this from the outlet pin fail the automatic check.',
    150,
    'm',
    5000,
  ),
  count(
    'onboarding.photo_max_age_days',
    'onboarding',
    'Photo age limit',
    'Photos older than this fail the automatic check.',
    30,
    'days',
    365,
  ),
  count(
    'onboarding.licence_min_valid_days',
    'onboarding',
    'Licence valid for at least',
    'A food licence must have at least this many days left to submit or go live.',
    7,
    'days',
    180,
  ),
  count(
    'onboarding.licence_reminder_days',
    'onboarding',
    'Licence expiry reminder',
    'Days before a licence expires that it is flagged on Home and in the queue.',
    30,
    'days',
    180,
  ),
  percent(
    'onboarding.bank_name_match_percent',
    'onboarding',
    'Bank name match',
    'How closely the bank account name must match the business or owner name before it is flagged.',
    60,
  ),
  {
    key: 'onboarding.four_eyes',
    group: 'onboarding',
    label: 'Second person checks',
    description:
      'Nobody may check a document they uploaded, or approve or take live an outlet they added.',
    type: 'boolean',
    scopes: ALL,
    seed: { global: true },
  },
  {
    key: 'onboarding.pause_on_licence_expiry',
    group: 'onboarding',
    label: 'Pause outlets when the licence expires',
    description: 'The daily check pauses a live outlet whose food licence has expired.',
    type: 'boolean',
    scopes: ALL,
    seed: { global: true },
  },

  /* Menus (W5) */
  count(
    'menu.review_sla_hours',
    'menu',
    'Ops checks menu changes within',
    'New dishes, price changes and renames wait for Ops at most this long; later ones show as late.',
    2,
    'h',
    72,
  ),
  {
    key: 'menu.instant_price_drop',
    group: 'menu',
    label: 'Price drops go live at once',
    description:
      'When on, a lower price goes live without a review. Price rises always wait for Ops.',
    type: 'boolean',
    scopes: ALL,
    seed: { global: false },
  },
  percent(
    'menu.typo_similarity_percent',
    'menu',
    'Spelling fix, not a rename',
    'A name change at least this similar to the old name is a spelling fix and goes live at once.',
    80,
  ),
  {
    key: 'menu.four_eyes',
    group: 'menu',
    label: 'Second person checks menu changes',
    description: 'Nobody may approve a menu change they made themselves.',
    type: 'boolean',
    scopes: ALL,
    seed: { global: true },
  },
  count(
    'menu.scan_max_items',
    'menu',
    'Most dishes read from one menu card',
    'The menu reader stops after this many dishes.',
    300,
    'dishes',
    1000,
  ),
  percent(
    'menu.parity_tolerance_percent',
    'menu',
    'Price check tolerance',
    'An app price this much above the menu card still counts as matching.',
    0,
  ),
  count(
    'menu.parity_fix_hours',
    'menu',
    'Time to fix a price mismatch',
    'After a first confirmed mismatch the restaurant has this long to fix its prices.',
    24,
    'h',
    168,
  ),
  percent(
    'menu.parity_rank_penalty_percent',
    'menu',
    'Ranking drop for a second mismatch',
    'How far the restaurant drops in ranking after a second confirmed mismatch.',
    30,
  ),
  count(
    'menu.parity_strike_window_days',
    'menu',
    'Mismatches counted over',
    'Confirmed mismatches older than this no longer count towards the next step.',
    90,
    'days',
    365,
  ),

  /* Riders */
  money(
    'riders.cash_limit_bike',
    'riders',
    'Cash limit — motorbike',
    'Most cash a motorbike rider may hold before only prepaid orders are offered.',
    2000,
    3200,
    true,
  ),
  money(
    'riders.cash_limit_walker',
    'riders',
    'Cash limit — walker',
    'Most cash a walking rider may hold.',
    500,
    800,
    true,
  ),
  money(
    'riders.cash_warn_bike',
    'riders',
    'Cash warning — motorbike',
    'The rider is asked to drop cash when they reach this amount.',
    1500,
    2400,
    true,
  ),
  count(
    'riders.max_cod_in_a_row',
    'riders',
    'Cash orders in a row',
    'Most cash-on-delivery orders a rider gets back to back.',
    3,
    'orders',
    20,
  ),
  minutes(
    'riders.cash_drop_deadline_minutes',
    'riders',
    'Cash drop deadline',
    'Minutes a rider over the cash limit has to drop cash before offers stop.',
    30,
    240,
  ),
  money(
    'riders.hub_overnight_cash_max',
    'riders',
    'Hub overnight cash limit',
    'Most cash a town hub may keep overnight.',
    10_000,
    16_000,
    true,
    rs(1_000_000),
  ),
  minutes(
    'riders.wait_pay_after_minutes',
    'riders',
    'Waiting pay starts after',
    'Riders are paid for waiting at the restaurant after this many minutes.',
    6,
    60,
  ),
  money(
    'riders.wait_pay_per_minute',
    'riders',
    'Waiting pay per minute',
    'Paid to the rider per minute of waiting (charged to the restaurant).',
    2,
    3,
    true,
  ),
  percent(
    'riders.stack_bonus_percent',
    'riders',
    'Stacked order bonus',
    'Extra pay for a second order on the same trip, as a share of the base pay.',
    70,
  ),
  money(
    'riders.dry_run_fee',
    'riders',
    'Dry-run fee',
    'Paid to the rider when the restaurant cancels after the rider was assigned.',
    20,
    30,
    true,
  ),
  count(
    'riders.delivery_unlock_distance_m',
    'riders',
    'Deliver unlocks within',
    'The rider can mark an order delivered (and enter the delivery code) only this close to the drop pin, by the latest GPS fix.',
    150,
    'm',
    2000,
  ),
  count(
    'riders.skip_pause_after',
    'riders',
    'Skips before a pause',
    'Skipped offers in a row before the rider is paused.',
    3,
    'skips',
    20,
  ),
  minutes(
    'riders.skip_pause_minutes',
    'riders',
    'Pause after skips',
    'Length of the pause after too many skipped offers.',
    30,
    240,
  ),
  money(
    'riders.cancel_at_restaurant_penalty',
    'riders',
    'Rider cancel at restaurant',
    'Charged when a rider cancels after reaching the restaurant.',
    40,
    65,
    true,
  ),
  money(
    'riders.kit_deposit',
    'riders',
    'Kit deposit',
    'Refundable deposit for the bag and shirts, taken from the first week’s pay.',
    500,
    800,
    true,
  ),
  count(
    'riders.safe_speed_kmh',
    'riders',
    'Safe speed used for estimates',
    'Travel estimates never assume a faster speed than this.',
    35,
    'km/h',
    80,
  ),
  count(
    'riders.milestone_1_orders',
    'riders',
    'Daily milestone 1 — orders',
    'Deliveries in a day for the first milestone bonus.',
    5,
    'orders',
    100,
  ),
  money(
    'riders.milestone_1_bonus',
    'riders',
    'Daily milestone 1 — bonus',
    'Bonus for reaching the first milestone.',
    50,
    80,
    true,
  ),
  count(
    'riders.milestone_2_orders',
    'riders',
    'Daily milestone 2 — orders',
    'Deliveries in a day for the second milestone bonus.',
    10,
    'orders',
    100,
  ),
  money(
    'riders.milestone_2_bonus',
    'riders',
    'Daily milestone 2 — bonus',
    'Bonus for reaching the second milestone.',
    120,
    190,
    true,
  ),
  count(
    'riders.fake_gps_suspension_hours',
    'riders',
    'Fake location suspension',
    'Hours a rider is suspended when a fake-GPS app is detected.',
    48,
    'h',
    720,
  ),

  /* Riders — W12 pay, shifts, onboarding */
  money(
    'riders.base_pay',
    'riders',
    'Trip pay',
    'Paid to the rider for every delivered trip.',
    22,
    35,
    true,
  ),
  money(
    'riders.per_km_pay',
    'riders',
    'Pay per km',
    'Paid per km from the restaurant to the customer beyond the free distance.',
    6,
    10,
    true,
  ),
  km(
    'riders.free_km',
    'riders',
    'Distance included in trip pay',
    'Km covered by the trip pay before per-km pay starts.',
    1.5,
  ),
  money(
    'riders.slot_guarantee',
    'riders',
    'Shift guarantee',
    'Lowest pay for a booked 3-hour shift when the rider meets the online and acceptance rules.',
    120,
    190,
    true,
  ),
  count(
    'riders.slot_hours',
    'riders',
    'Shift length',
    'Default length of a bookable shift.',
    3,
    'h',
    12,
  ),
  minutes(
    'riders.slot_min_online_minutes',
    'riders',
    'Online time for the guarantee',
    'Minutes online inside a booked shift needed to earn the guarantee.',
    165,
    720,
  ),
  percent(
    'riders.slot_min_acceptance_percent',
    'riders',
    'Acceptance for the guarantee',
    'Share of offers accepted inside a booked shift needed to earn the guarantee.',
    90,
  ),
  minutes(
    'riders.slot_cancel_cutoff_minutes',
    'riders',
    'Shift cancel cut-off',
    'Riders can cancel a booked shift until this long before it starts.',
    60,
    1440,
  ),
  percent(
    'riders.quiz_pass_percent',
    'riders',
    'Training quiz pass mark',
    'Score needed to pass the onboarding quiz.',
    80,
  ),
  count(
    'riders.police_clearance_grace_days',
    'riders',
    'Police clearance grace',
    'Days after approval a rider may work with only the police clearance receipt.',
    14,
    'days',
    90,
  ),
  money(
    'riders.kit_deposit_per_day_max',
    'riders',
    'Kit deposit recovered per day',
    'Most of the kit deposit taken from one daily statement.',
    100,
    160,
    true,
  ),
  money(
    'riders.insurance_premium_per_trip',
    'riders',
    'Capsico Suraksha premium per trip',
    'Insurance premium Capsico pays per delivered trip (not taken from the rider).',
    0.6,
    1,
    true,
  ),
  {
    key: 'riders.tds_basis_points',
    group: 'riders',
    label: 'Tax withheld from rider payouts',
    description:
      'Income tax withheld from rider statements, in basis points (India section 194C). 0 keeps it off until the CA confirms.',
    type: 'integer',
    unit: 'bp',
    min: 0,
    max: 1000,
    scopes: COUNTRY_ONLY,
    seed: { IN: 0, NP: 0 },
  },

  /* Cash on delivery */
  money(
    'cod.new_customer_max',
    'cod',
    'Cash limit for new customers',
    'Largest cash-on-delivery order for customers with few delivered orders.',
    400,
    600,
  ),
  count(
    'cod.new_customer_orders',
    'cod',
    'New customer until',
    'Customers with fewer delivered orders than this count as new.',
    2,
    'orders',
    50,
  ),
  percent(
    'cod.risky_refusal_percent',
    'cod',
    'Cash off above refusal rate',
    'Customers who refuse more than this share of cash orders lose cash on delivery.',
    8,
  ),
  money(
    'cod.unreachable_penalty',
    'cod',
    'Cash order unreachable penalty',
    'Charged to a cash customer who could not be reached; checkout is locked until paid.',
    100,
    150,
  ),

  /* Trust & safety */
  count(
    'trust.max_accounts_per_device',
    'trust',
    'Accounts per device',
    'Most customer accounts allowed on one device.',
    2,
    'accounts',
    10,
    GLOBAL_COUNTRY,
  ),
  count(
    'trust.max_new_numbers_30d',
    'trust',
    'New phone numbers per device (30 days)',
    'More new numbers than this on one device in 30 days freezes checkout.',
    2,
    'numbers',
    10,
    GLOBAL_COUNTRY,
  ),
  percent(
    'trust.refund_claim_ratio_percent',
    'trust',
    'Refund claims sent to an agent above',
    'Customers whose claims exceed this share of orders get an agent instead of an instant refund.',
    8,
    GLOBAL_COUNTRY,
  ),
  count(
    'trust.review_window_hours',
    'trust',
    'Review window',
    'Hours after delivery a customer may leave a review.',
    72,
    'h',
    720,
    GLOBAL_COUNTRY,
  ),
  count(
    'trust.review_edit_hours',
    'trust',
    'Review editable for',
    'Hours a customer (and a restaurant reply) can still be edited after posting.',
    24,
    'h',
    168,
    GLOBAL_COUNTRY,
  ),
  count(
    'trust.review_prior_count',
    'trust',
    'Rating prior weight',
    'Pretend ratings blended into every restaurant average so a few reviews cannot swing it.',
    10,
    'ratings',
    100,
    GLOBAL_COUNTRY,
  ),
  {
    key: 'trust.review_prior_mean',
    group: 'trust',
    label: 'Rating prior average',
    description: 'The average those pretend ratings carry.',
    type: 'decimal',
    unit: 'stars',
    min: 1,
    max: 5,
    scopes: GLOBAL_COUNTRY,
    seed: { global: 4 },
  },
  count(
    'trust.review_min_count',
    'trust',
    'Show "New" below',
    'Restaurants with fewer counted ratings show "New" instead of a number.',
    5,
    'ratings',
    100,
    GLOBAL_COUNTRY,
  ),
  count(
    'trust.review_days',
    'trust',
    'Rating window',
    'Only ratings from this many days count towards the restaurant average.',
    90,
    'days',
    365,
    GLOBAL_COUNTRY,
  ),
  count(
    'trust.review_spike_count',
    'trust',
    '1-star spike: ratings',
    'This many 1-star food ratings inside the spike window put new low ratings on hold for Ops.',
    5,
    'ratings',
    50,
    GLOBAL_COUNTRY,
  ),
  count(
    'trust.review_spike_hours',
    'trust',
    '1-star spike: window',
    'Hours the 1-star spike counts over.',
    2,
    'h',
    48,
    GLOBAL_COUNTRY,
  ),
  count(
    'trust.dispute_hours',
    'trust',
    'Dispute decision time',
    'Hours Ops have to decide a restaurant dispute.',
    24,
    'h',
    168,
    GLOBAL_COUNTRY,
  ),
  count(
    'trust.bestseller_min_likes',
    'trust',
    'Bestseller needs likes',
    'A dish needs at least this many likes (90 days) to earn the bestseller tag from likes.',
    10,
    'likes',
    1000,
    GLOBAL_COUNTRY,
  ),
  count(
    'trust.self_order_m',
    'trust',
    'Self-order distance',
    'Orders placed this close to the outlet from the same network as its Partner app count as self-orders.',
    15,
    'm',
    500,
    GLOBAL_COUNTRY,
  ),
  percent(
    'trust.self_order_surcharge_percent',
    'trust',
    'Self-order surcharge',
    'Charged on the restaurant statement for every self-order, as a share of the food total.',
    15,
    GLOBAL_COUNTRY,
  ),
  count(
    'trust.risk_amber',
    'trust',
    'Refund risk: watch from',
    'Risk scores from this value need a photo and an agent.',
    40,
    'points',
    100,
    GLOBAL_COUNTRY,
  ),
  count(
    'trust.risk_red',
    'trust',
    'Refund risk: high from',
    'Risk scores from this value block bot refunds; an agent must decide.',
    70,
    'points',
    100,
    GLOBAL_COUNTRY,
  ),
  count(
    'trust.integrity_top_slots_min',
    'trust',
    'Top slots need integrity',
    'Restaurants below this integrity score are kept out of the top rows.',
    70,
    'points',
    100,
    GLOBAL_COUNTRY,
  ),
  count(
    'trust.hygiene_window_days',
    'trust',
    'Hygiene window',
    'Days of spills, seal issues and foreign objects counted in the hygiene score.',
    90,
    'days',
    365,
    GLOBAL_COUNTRY,
  ),
  percent(
    'trust.reaudit_quality_percent',
    'trust',
    'Re-audit: quality complaints',
    'A share of quality complaints above this (re-audit window) opens a re-audit.',
    4,
    GLOBAL_COUNTRY,
  ),
  count(
    'trust.reaudit_spill_count',
    'trust',
    'Re-audit: seal spills',
    'More confirmed seal spills than this (re-audit window) open a re-audit.',
    3,
    'spills',
    50,
    GLOBAL_COUNTRY,
  ),
  count(
    'trust.reaudit_window_days',
    'trust',
    'Re-audit window',
    'Days the re-audit triggers count over.',
    14,
    'days',
    90,
    GLOBAL_COUNTRY,
  ),
  count(
    'trust.reaudit_fix_hours',
    'trust',
    'Re-audit fix time',
    'Hours a restaurant has to fix a re-audit.',
    48,
    'h',
    336,
    GLOBAL_COUNTRY,
  ),
  percent(
    'trust.reaudit_rank_penalty_percent',
    'trust',
    'Re-audit ranking drop',
    'How far an outlet drops in ranking while a re-audit is open.',
    30,
    GLOBAL_COUNTRY,
  ),
  count(
    'trust.rider_rating_window',
    'trust',
    'Rider rating window',
    'Rider rating is the mean of this many most recent rated deliveries.',
    100,
    'deliveries',
    1000,
    GLOBAL_COUNTRY,
  ),
  {
    key: 'trust.rider_new_rating',
    group: 'trust',
    label: 'New rider rating',
    description: 'Neutral rating a rider starts with until enough deliveries are rated.',
    type: 'decimal',
    unit: 'stars',
    min: 1,
    max: 5,
    scopes: GLOBAL_COUNTRY,
    seed: { global: 4.5 },
  },
  count(
    'trust.rider_code_mismatch_limit',
    'trust',
    'Rider code mismatches',
    'Delivery-code mismatches in 30 days that put a rider on the risk list.',
    3,
    'mismatches',
    50,
    GLOBAL_COUNTRY,
  ),

  /* Compensation & credits */
  money(
    'compensation.late_credit',
    'compensation',
    'Late delivery credit',
    'Store credit given when an order arrives well past the top of its time range.',
    40,
    65,
    true,
  ),
  minutes(
    'compensation.late_after_minutes',
    'compensation',
    'Late credit after',
    'Minutes past the top of the shown range before the late credit is given.',
    15,
    120,
  ),
  money(
    'compensation.restaurant_timeout_credit',
    'compensation',
    'Credit when a restaurant doesn’t accept',
    'Store credit when the order is cancelled because the restaurant did not accept.',
    30,
    30,
  ),
  count(
    'compensation.restaurant_timeout_credit_hours',
    'compensation',
    'That credit is valid for',
    'Hours the restaurant-timeout credit stays usable.',
    2,
    'h',
    720,
  ),
  money(
    'compensation.no_rider_cancel_credit',
    'compensation',
    'Credit when no rider is found',
    'Store credit when the order is cancelled because no rider accepted.',
    50,
    75,
  ),
  money(
    'compensation.merchant_cancel_credit',
    'compensation',
    'Credit when the restaurant cancels',
    'Store credit when the restaurant cancels an accepted order.',
    50,
    75,
    true,
  ),

  /* Delivery time estimates */
  minutes(
    'eta.range_below_minutes',
    'eta',
    'Shown range — minutes below',
    'The time shown to customers starts this many minutes below the estimate.',
    3,
    30,
  ),
  minutes(
    'eta.range_above_minutes',
    'eta',
    'Shown range — minutes above',
    'The time shown to customers ends this many minutes above the estimate.',
    7,
    60,
  ),
  minutes(
    'eta.floor_minutes',
    'eta',
    'Shortest time shown',
    'The shown time range never starts below this.',
    15,
    60,
  ),
  minutes('eta.buffer_minutes', 'eta', 'Safety buffer', 'Added to every estimate.', 5, 30),
  {
    key: 'eta.travel_speed_kmph',
    group: 'eta',
    label: 'Average rider speed',
    description:
      'Used to turn distance into travel time for estimates before road routing is switched on.',
    type: 'decimal',
    unit: 'km/h',
    min: 5,
    max: 60,
    scopes: ALL,
    seed: { global: 18 },
  },
  minutes(
    'eta.rain_extra_minutes',
    'eta',
    'Extra time in rain mode',
    'Added to estimates in rain mode.',
    15,
    90,
  ),
  minutes(
    'eta.festival_extra_minutes',
    'eta',
    'Extra time in festival mode',
    'Added to estimates in festival mode.',
    25,
    120,
  ),

  /* Live tracking & delivery proof */
  count(
    'tracking.nearby_m',
    'tracking',
    'Rider is nearby within',
    'After pickup, the customer is told "Rider is nearby" when the rider comes this close.',
    500,
    'm',
    5000,
  ),
  count(
    'tracking.max_speed_kmh',
    'tracking',
    'Fastest believable speed',
    'GPS points implying a faster speed than this are flagged as possible fake location.',
    120,
    'km/h',
    300,
  ),
  count(
    'tracking.retention_days',
    'tracking',
    'Keep rider GPS history for',
    'Raw rider location points older than this are deleted by the daily job.',
    30,
    'days',
    365,
  ),

  /* Growth & marketing */
  percent(
    'growth.cashback_percent',
    'growth',
    'Cashback',
    'Store credit given back on eligible orders (paid by Capsico).',
    5,
  ),
  money(
    'growth.cashback_min_order',
    'growth',
    'Cashback on orders above',
    'Orders must be worth more than this to earn cashback.',
    200,
    300,
  ),
  count(
    'growth.promo_credit_expiry_days',
    'growth',
    'Promotional credit expires after',
    'Days before promotional and compensation credit expires.',
    30,
    'days',
    365,
  ),
  count(
    'growth.marketing_msgs_per_week',
    'growth',
    'Marketing messages per week',
    'Most marketing messages per device per week (push, SMS and WhatsApp together).',
    3,
    'messages',
    20,
  ),
  clock(
    'growth.quiet_hours_from',
    'growth',
    'Quiet hours start',
    'No marketing messages from this time…',
    at(22),
  ),
  clock('growth.quiet_hours_to', 'growth', 'Quiet hours end', '…until this time.', at(11)),
  count(
    'growth.winback_after_days',
    'growth',
    'Win-back after',
    'Days without an order before the win-back offer is sent.',
    30,
    'days',
    365,
  ),
  money(
    'growth.winback_credit',
    'growth',
    'Win-back credit',
    'Store credit in the win-back offer.',
    50,
    75,
  ),
  count(
    'growth.winback_credit_days',
    'growth',
    'Win-back credit valid for',
    'Days the win-back credit stays usable.',
    7,
    'days',
    90,
  ),
  money(
    'growth.launch_budget_per_town',
    'growth',
    'Launch free-delivery budget per town',
    'Capsico-funded free delivery budget for a new town.',
    60_000,
    96_000,
    false,
    rs(10_000_000),
  ),
  count(
    'growth.launch_days',
    'growth',
    'Launch offer runs for',
    'Days the launch free-delivery offer runs (or until the customer cap).',
    60,
    'days',
    365,
  ),
  count(
    'growth.launch_max_customers',
    'growth',
    'Launch offer customer cap',
    'New customers covered by the launch offer per town.',
    1000,
    'customers',
    100_000,
  ),
  /* W18 growth: cashback, win-back, referrals, campus, attribution */
  money(
    'growth.cashback_cap',
    'growth',
    'Cashback cap per order',
    'Most cashback one delivered order can earn.',
    50,
    80,
    true,
  ),
  count(
    'growth.winback_once_days',
    'growth',
    'Win-back at most every',
    'Days before the same customer can get the win-back credit and offer again.',
    90,
    'days',
    365,
    GLOBAL_COUNTRY,
  ),
  money(
    'growth.referral_reward',
    'growth',
    'Referral reward',
    'Capsico Money the inviter gets after a friend’s first delivered order.',
    50,
    80,
  ),
  count(
    'growth.referral_reward_days',
    'growth',
    'Referral reward valid for',
    'Days the referral reward stays usable.',
    30,
    'days',
    365,
    GLOBAL_COUNTRY,
  ),
  count(
    'growth.referral_max_per_month',
    'growth',
    'Referral rewards per month',
    'Most referral rewards one inviter can earn in a calendar month.',
    10,
    'rewards',
    100,
    GLOBAL_COUNTRY,
  ),
  count(
    'growth.attribution_days',
    'growth',
    'Attribution window',
    'Days after sign-up a referral, creator, ambassador or QR code can still be credited.',
    7,
    'days',
    60,
    GLOBAL_COUNTRY,
  ),
  km(
    'growth.campus_free_km',
    'growth',
    'Campus Pass free-delivery radius',
    'Campus Pass holders pay no delivery fee within this distance of their campus.',
    1.5,
  ),
  count(
    'growth.campus_pass_days',
    'growth',
    'Campus Pass valid for',
    'Days a verified Campus Pass lasts before the student verifies again.',
    90,
    'days',
    365,
    GLOBAL_COUNTRY,
  ),
  money(
    'growth.ambassador_reward',
    'growth',
    'Ambassador reward per verified student',
    'Paid to a campus ambassador for each Campus Pass verified with their code.',
    20,
    30,
    true,
  ),
  /* Ads */
  money(
    'ads.sponsored_listing_per_day',
    'ads',
    'Sponsored listing price',
    'Price per day per zone for a sponsored listing.',
    99,
    150,
  ),
  money(
    'ads.banner_per_week',
    'ads',
    'Banner price',
    'Price per week per zone for a banner.',
    499,
    750,
  ),
  count(
    'ads.max_sponsored_per_screen',
    'ads',
    'Sponsored listings per screen',
    'Most sponsored listings shown on one screen.',
    3,
    'listings',
    20,
  ),
  count(
    'ads.max_banners_per_zone',
    'ads',
    'Banners per zone',
    'Most banners running in one zone.',
    5,
    'banners',
    50,
  ),
  count(
    'ads.min_hygiene_score',
    'ads',
    'Ads need hygiene score of',
    'Restaurants below this hygiene score cannot buy ads.',
    75,
    'points',
    100,
  ),
  percent(
    'ads.max_rejection_percent',
    'ads',
    'Ads blocked above rejection rate',
    'Restaurants rejecting more than this share of orders cannot buy ads.',
    5,
  ),
  count(
    'ads.min_town_orders_per_day',
    'ads',
    'Ads open in a town at',
    'Ads are switched on in a town once it reaches this many orders per day.',
    1000,
    'orders/day',
    1_000_000,
  ),

  /* Partner plans & billing (W6) */
  {
    key: 'subscriptions.default_plan',
    group: 'subscriptions',
    label: 'Plan after the free launch',
    description:
      'Plan an outlet moves to when its free launch ends, if the owner has not picked one.',
    type: 'enum',
    options: ['starter', 'growth', 'town_hero', 'legend'],
    scopes: GLOBAL_COUNTRY,
    seed: { global: 'starter' },
  },
  count(
    'subscriptions.invoice_due_days',
    'subscriptions',
    'Bill due within',
    'Days a restaurant has to pay a monthly bill before it shows as past due.',
    7,
    'days',
    60,
    GLOBAL_COUNTRY,
  ),
  percent(
    'subscriptions.upgrade_nudge_percent',
    'subscriptions',
    'Suggest a bigger plan from',
    'When an outlet has used this much of its monthly order allowance, the app suggests the next plan.',
    80,
    GLOBAL_COUNTRY,
  ),
  percent(
    'subscriptions.commission_max_percent',
    'subscriptions',
    'Highest voluntary commission',
    'The most a restaurant may choose to pay as voluntary commission on its orders.',
    30,
    GLOBAL_COUNTRY,
  ),

  /* Payouts */
  clock(
    'payouts.restaurant_run_at',
    'payouts',
    'Restaurant payout run',
    'Daily time restaurant payouts are sent.',
    at(23, 30),
  ),
  clock(
    'payouts.rider_run_at',
    'payouts',
    'Rider payout run',
    'Daily time rider payouts are sent.',
    at(7),
  ),

  /* Payments & Capsico Money (W11) */
  minutes(
    'payments.intent_timeout_minutes',
    'payments',
    'Time to finish an online payment',
    'An online order waits this long for the payment; then it is cancelled and nothing is charged.',
    15,
    60,
    GLOBAL_COUNTRY,
  ),
  count(
    'payments.wallet_credit_days',
    'payments',
    'Capsico Money valid for',
    'Days a Capsico Money credit (refunds, bonuses) can be used before it expires.',
    30,
    'days',
    365,
    GLOBAL_COUNTRY,
  ),
  money(
    'payments.wallet_choice_bonus',
    'payments',
    'Bonus for choosing Capsico Money',
    'Extra credit when a customer takes an approved complaint refund as Capsico Money instead of the original method.',
    50,
    80,
    true,
  ),
  count(
    'payments.refund_choice_hours',
    'payments',
    'Time to pick a refund method',
    'After this many hours an unanswered complaint refund goes to Capsico Money (with the bonus).',
    24,
    'h',
    168,
    GLOBAL_COUNTRY,
  ),
  {
    key: 'payouts.tds_basis_points',
    group: 'payouts',
    label: 'Tax withheld from payouts',
    description:
      'Income tax withheld from each restaurant payout, in basis points (10 = 0.1%, India section 194-O). 0 turns it off.',
    type: 'integer',
    unit: 'bp',
    min: 0,
    max: 1000,
    scopes: COUNTRY_ONLY,
    seed: { IN: 10, NP: 0 },
  },
  money(
    'payouts.minimum_payout',
    'payouts',
    'Smallest payout',
    'Statements below this are carried into the next night instead of being paid.',
    100,
    150,
    true,
  ),

  /* Tax */
  {
    key: 'tax.food_rate_percent',
    group: 'tax',
    label: 'Tax on food',
    description: 'GST on food collected by Capsico (India) or VAT on food (Nepal).',
    type: 'percent',
    unit: '%',
    min: 0,
    max: 100,
    scopes: COUNTRY_ONLY,
    seed: { IN: 5, NP: 13 },
  },
  {
    key: 'tax.fee_rate_percent',
    group: 'tax',
    label: 'Tax on Capsico fees',
    description: 'GST (India) or VAT (Nepal) on subscriptions, commissions and customer fees.',
    type: 'percent',
    unit: '%',
    min: 0,
    max: 100,
    scopes: COUNTRY_ONLY,
    seed: { IN: 18, NP: 13 },
  },

  /* Search & discovery — ranking weights are relative points; only their ratio matters. */
  count(
    'discovery.weight_distance',
    'discovery',
    'Ranking weight — distance',
    'How much being close to the customer counts in restaurant ranking.',
    30,
    'pts',
    100,
  ),
  count(
    'discovery.weight_eta',
    'discovery',
    'Ranking weight — delivery time',
    'How much a short estimated delivery time counts.',
    20,
    'pts',
    100,
  ),
  count(
    'discovery.weight_rating',
    'discovery',
    'Ranking weight — rating',
    'How much the food rating counts (new restaurants start at the rating prior).',
    20,
    'pts',
    100,
  ),
  count(
    'discovery.weight_popularity',
    'discovery',
    'Ranking weight — popularity',
    'How much delivered orders in the last 30 days count.',
    15,
    'pts',
    100,
  ),
  count(
    'discovery.weight_conversion',
    'discovery',
    'Ranking weight — conversion',
    'How much menu-open to order conversion counts. Used once the customer app records menu opens.',
    5,
    'pts',
    100,
  ),
  count(
    'discovery.weight_local_legend',
    'discovery',
    'Ranking weight — Local Legend',
    'Extra points for Local Legend restaurants.',
    10,
    'pts',
    100,
  ),
  count(
    'discovery.weight_personal',
    'discovery',
    'Ranking weight — personal history',
    "How much a customer's own past orders count. Used once customers sign in.",
    10,
    'pts',
    100,
  ),
  count(
    'discovery.weight_text',
    'discovery',
    'Ranking weight — search match',
    'When a customer types a search, how much the closeness of the match counts.',
    60,
    'pts',
    100,
  ),
  {
    key: 'discovery.rating_prior',
    group: 'discovery',
    label: 'Starting rating',
    description:
      'Rating assumed for restaurants with few ratings, so one review cannot push them to the top or bottom.',
    type: 'decimal',
    unit: '★',
    min: 1,
    max: 5,
    scopes: ALL,
    seed: { global: 3.8 },
  },
  count(
    'discovery.rating_min_count',
    'discovery',
    'Ratings before trusting the average',
    'Below this many ratings the average is blended with the starting rating.',
    5,
    'ratings',
    100,
  ),
  minutes(
    'discovery.fast_minutes',
    'discovery',
    '“Fast delivery” filter',
    'Restaurants whose estimated delivery is within this many minutes show under Fast delivery.',
    30,
    120,
  ),
  money(
    'discovery.price_two_low_max',
    'discovery',
    'Price for two — budget band up to',
    'Cost for two at or below this is the budget band.',
    300,
    500,
  ),
  money(
    'discovery.price_two_mid_max',
    'discovery',
    'Price for two — mid band up to',
    'Cost for two at or below this (and above the budget band) is the mid band.',
    600,
    1000,
  ),

  /* Checkout (W10). Amounts marked estimated are starting values to agree before launch. */
  money(
    'fees.min_order_value',
    'fees',
    'Minimum order value',
    'Food total (before fees and offers) below which an order cannot be placed.',
    99,
    150,
    true,
  ),
  money(
    'fees.small_order_below',
    'fees',
    'Small-order fee applies below',
    'Food totals under this pay the small-order fee.',
    149,
    250,
    true,
  ),
  money(
    'fees.small_order_fee',
    'fees',
    'Small-order fee',
    'Charged on small orders so a rider trip still covers its cost.',
    15,
    25,
    true,
  ),
  money(
    'fees.platform_fee',
    'fees',
    'Platform fee',
    'Per-order platform fee. 0 keeps the "zero platform fee" promise and hides the bill line.',
    0,
    0,
  ),
  money(
    'fees.delivery_base',
    'fees',
    'Delivery fee — base',
    'Delivery fee for trips up to the base distance.',
    15,
    25,
    true,
  ),
  km(
    'fees.delivery_base_km',
    'fees',
    'Delivery fee — base distance',
    'Road distance covered by the base delivery fee.',
    2,
  ),
  money(
    'fees.delivery_per_km',
    'fees',
    'Delivery fee — per extra km',
    'Added for each road kilometre (or part) beyond the base distance.',
    5,
    8,
    true,
  ),
  {
    key: 'fees.road_factor',
    group: 'fees',
    label: 'Road distance factor',
    description:
      'Straight-line distance × this ≈ road distance, until road routing is switched on.',
    type: 'decimal',
    unit: '×',
    min: 1,
    max: 3,
    scopes: ALL,
    seed: { global: 1.3 },
  },
  money(
    'fees.tip_preset_1',
    'fees',
    'Tip choice 1',
    'First tip button at checkout. Tips go 100% to the rider.',
    10,
    20,
    true,
  ),
  money(
    'fees.tip_preset_2',
    'fees',
    'Tip choice 2',
    'Second tip button at checkout.',
    20,
    30,
    true,
  ),
  money('fees.tip_preset_3', 'fees', 'Tip choice 3', 'Third tip button at checkout.', 30, 50, true),
  money(
    'fees.tip_max',
    'fees',
    'Highest tip',
    'Largest tip a customer can add at checkout.',
    200,
    300,
    true,
  ),
  count(
    'growth.free_delivery_orders',
    'growth',
    'Free delivery for first orders',
    "A new customer's first orders have no delivery fee (Capsico pays, from the town's launch budget).",
    3,
    'orders',
    10,
  ),
  minutes(
    'orders.schedule_lead_minutes',
    'orders',
    'Scheduled orders — earliest slot',
    'A scheduled order must be at least this far ahead.',
    60,
    1440,
  ),
  count(
    'orders.schedule_max_days',
    'orders',
    'Scheduled orders — furthest ahead',
    'Customers can schedule up to this many days ahead.',
    2,
    'days',
    7,
  ),
  money(
    'orders.schedule_min_value',
    'orders',
    'Scheduled orders — minimum food total',
    'Scheduled orders below this food total are refused.',
    199,
    300,
    true,
  ),
  /* Control tower (W15) */
  count(
    'tower.busy_kitchen_orders',
    'tower',
    'Kitchen counts as busy at',
    'An outlet with this many live orders gets extra prep time automatically (Ops can undo).',
    5,
    'orders',
    50,
  ),
  minutes(
    'tower.busy_kitchen_extra_minutes',
    'tower',
    'Extra prep time for a busy kitchen',
    'Added to the kitchen’s prep time while it stays busy; removed once it drops below the threshold.',
    10,
    60,
  ),
  minutes(
    'tower.unaccepted_alert_minutes',
    'tower',
    'Alert when an order is not accepted within',
    'The Fleet Captain is alerted when a placed order waits this long (the order ladder still owns auto-cancel).',
    3,
    30,
  ),
  minutes(
    'tower.no_rider_alert_minutes',
    'tower',
    'Count an order as “no rider” after',
    'Orders searching for a rider longer than this show on the no-rider card.',
    8,
    60,
  ),
  minutes(
    'tower.rain_mode_minutes',
    'tower',
    'Rain mode runs for',
    'Rain mode switched on from the weather lasts this long and is extended while it keeps raining.',
    60,
    360,
    ['global', 'country', 'town'],
  ),
  {
    key: 'tower.rain_mm_per_hour',
    group: 'tower',
    label: 'Rain starts at',
    description: 'Rainfall at or above this (mm per hour) turns on rain mode for the town.',
    type: 'decimal',
    unit: 'mm/h',
    min: 0.1,
    max: 50,
    scopes: ['global', 'country', 'town'],
    seed: { global: 0.5 },
  },
  {
    key: 'tower.heavy_rain_mm_per_hour',
    group: 'tower',
    label: 'Heavy rain starts at',
    description:
      'Rainfall at or above this (mm per hour) also stops offers to walkers and cyclists in the town.',
    type: 'decimal',
    unit: 'mm/h',
    min: 0.5,
    max: 100,
    scopes: ['global', 'country', 'town'],
    seed: { global: 4 },
  },
  percent(
    'tower.late_alert_percent',
    'tower',
    'Alert when late orders pass',
    'The Fleet Captain is alerted when this share of the last hour’s deliveries in a zone were late.',
    15,
  ),
  minutes(
    'tower.undo_minutes',
    'tower',
    'Undo window',
    'How long Ops and Captains can undo an automatic action or a switch flip.',
    30,
    240,
    GLOBAL_COUNTRY,
  ),
  clock(
    'tower.festival_suggest_at',
    'tower',
    'Festival mode starts at',
    'On a festival day, festival mode is switched on automatically at this local time (undoable).',
    at(17),
  ),
  minutes(
    'tower.weather_poll_minutes',
    'tower',
    'Check the weather every',
    'How often the control tower reads rainfall for each town.',
    10,
    120,
    GLOBAL_COUNTRY,
  ),
  /* Support & refunds (W16) */
  clock(
    'support.hours_open',
    'support',
    'Support desk opens at',
    'Agents answer from this local time. Outside desk hours the bot still answers and tickets wait for the morning.',
    at(10, 30),
  ),
  clock(
    'support.hours_close',
    'support',
    'Support desk closes at',
    'Agents stop answering at this local time (00:30 means half past midnight, after the late-night rush).',
    at(0, 30),
  ),
  minutes(
    'support.agent_sla_minutes',
    'support',
    'First agent reply within',
    'A ticket handed to an agent should get its first reply within this many minutes; later replies count as SLA breaches.',
    5,
    120,
    GLOBAL_COUNTRY,
  ),
  percent(
    'support.claim_ratio_review_percent',
    'support',
    'Review customers who claim on more than',
    'Customers whose refund claims in the last 30 days exceed this share of their orders always go to an agent.',
    8,
    GLOBAL_COUNTRY,
  ),
  money(
    'support.auto_refund_max',
    'support',
    'Bot refunds at most',
    'The assistant refunds missing items on its own only up to this amount; anything above goes to an agent.',
    300,
    480,
  ),
  money(
    'support.agent_refund_limit',
    'support',
    'Agent refund limit (one action)',
    'An agent can refund up to this much in one action; above it a support lead must approve.',
    500,
    800,
  ),
  money(
    'support.rider_charge_cap',
    'support',
    'Most a rider is charged per order',
    'When the rider is at fault, food cost plus trip pay is taken from their earnings, capped at this amount.',
    150,
    240,
  ),
  count(
    'support.max_concurrent_chats',
    'support',
    'Chats per agent at once',
    'The assignment engine never gives an agent more open chats than this.',
    4,
    'chats',
    20,
    GLOBAL_COUNTRY,
  ),
  minutes(
    'support.idle_reassign_minutes',
    'support',
    'Reassign when the agent is gone for',
    'Tickets assigned to an agent who went offline go back to the queue after this many idle minutes.',
    10,
    120,
    GLOBAL_COUNTRY,
  ),
  minutes(
    'support.auto_close_minutes',
    'support',
    'Close a quiet chat after',
    'Tickets waiting for the customer (or resolved) are closed after this long without a reply.',
    1440,
    10_080,
    GLOBAL_COUNTRY,
  ),
  count(
    'support.foreign_object_suspend_count',
    'support',
    'Propose a restaurant suspension after',
    'Confirmed foreign-object complaints within 30 days that make support propose a suspension (Ops confirm).',
    3,
    'complaints',
    20,
    GLOBAL_COUNTRY,
  ),
  /* Market Initialers (W20) */
  count(
    'mi.daily_visit_quota',
    'mi',
    'Daily visit quota per MI',
    'Prospect visits each Market Initialer should log per day. A Lead can set a different quota for one MI.',
    25,
    'visits',
    200,
  ),
  count(
    'mi.visit_radius_m',
    'mi',
    'Visit radius',
    'A visit counts as on-site within this distance of the prospect; further away the MI must give a reason.',
    75,
    'm',
    1000,
  ),
  count(
    'mi.duplicate_radius_m',
    'mi',
    'Duplicate check radius',
    'A new prospect this close to one with a similar name (or with the same phone anywhere) is flagged as a possible duplicate.',
    30,
    'm',
    500,
  ),
  count(
    'mi.heartbeat_seconds',
    'mi',
    'Location heartbeat',
    'How often the MI app sends a location sample while on duty.',
    120,
    'seconds',
    900,
    GLOBAL_COUNTRY,
  ),
  money(
    'mi.pay_per_live',
    'mi',
    'Pay per live restaurant',
    'Credited to the MI who handed the restaurant off once it goes live on Capsico.',
    300,
    480,
  ),
  money(
    'mi.bonus_at_30_orders',
    'mi',
    'Order milestone bonus',
    'Extra pay when a handed-off restaurant reaches the milestone of delivered orders within the bonus window.',
    200,
    320,
  ),
  count(
    'mi.bonus_orders',
    'mi',
    'Bonus milestone',
    'Delivered orders a handed-off restaurant needs for the MI bonus.',
    30,
    'orders',
    1000,
    GLOBAL_COUNTRY,
  ),
  count(
    'mi.bonus_window_days',
    'mi',
    'Bonus window',
    'Days after going live in which the restaurant must reach the order milestone.',
    60,
    'days',
    365,
    GLOBAL_COUNTRY,
  ),
  count(
    'mi.clawback_days',
    'mi',
    'Clawback window',
    'If the restaurant leaves Capsico within this many days of going live, the MI pay is taken back.',
    14,
    'days',
    90,
    GLOBAL_COUNTRY,
  ),
  money(
    'mi.min_payout',
    'mi',
    'Smallest MI payout',
    'Weekly MI pay below this amount is carried to the next week.',
    100,
    160,
    true,
  ),
  /* Legal + grievance officer (W21). "To be appointed" until Capsico names the officers. */
  text(
    'legal.grievance_name',
    'legal',
    'Grievance officer name',
    'Shown on the Grievance page of the site and in the apps. India: required by the IT Rules and the DPDP Act; Nepal: the complaints contact.',
    'To be appointed',
    'To be appointed',
    120,
  ),
  text(
    'legal.grievance_email',
    'legal',
    'Grievance officer email',
    'Where customers, restaurants and riders send complaints and privacy requests.',
    'To be appointed',
    'To be appointed',
    120,
  ),
  text(
    'legal.grievance_phone',
    'legal',
    'Grievance officer phone',
    'Phone number for complaints, with country code.',
    'To be appointed',
    'To be appointed',
    40,
  ),
  text(
    'legal.grievance_address',
    'legal',
    'Grievance officer address',
    'Postal address of the company office that handles complaints.',
    'To be appointed',
    'To be appointed',
    200,
  ),
  /* Pilot gates (W23). Town scope: each town can carry its own bar before ordering goes live. */
  count(
    'launch.gate_paying_restaurants_min',
    'launch',
    'Paying restaurants needed',
    'Hard gate: outlets on an active paid plan (not the free launch period) before ordering goes live in a town.',
    15,
    'restaurants',
    1000,
  ),
  count(
    'launch.gate_orders_per_day_min',
    'launch',
    'Orders per day needed',
    'Hard gate: delivered app orders per day (7-day average) before ordering goes live.',
    150,
    'orders/day',
    100000,
  ),
  percent(
    'launch.gate_no_rider_cancel_max_percent',
    'launch',
    'Most no-rider cancels allowed',
    'Hard gate: orders cancelled because no rider was found, last 7 days.',
    2,
  ),
  percent(
    'launch.gate_on_time_min_percent',
    'launch',
    'On-time deliveries needed',
    'Soft gate: delivered by the promised time, last 7 days.',
    85,
  ),
  percent(
    'launch.gate_refund_max_percent',
    'launch',
    'Most refunds allowed',
    'Soft gate: orders with a refund, last 7 days.',
    5,
  ),
  count(
    'launch.gate_rider_supply_min',
    'launch',
    'Rider supply needed',
    'Soft gate: riders who delivered per 100 delivered orders a day, last 7 days.',
    5,
    'riders / 100 orders',
    100,
  ),
  percent(
    'launch.gate_support_sla_min_percent',
    'launch',
    'Support SLA met needed',
    'Soft gate: help tickets answered within their SLA, last 7 days.',
    90,
  ),
  percent(
    'launch.gate_crash_free_min_percent',
    'launch',
    'Crash-free sessions needed',
    'Soft gate: crash-free sessions, entered by hand until app telemetry exists.',
    99,
  ),
  /* Edge + app integrity (W24). Global only: one policy for every app. */
  {
    key: 'integrity.mode',
    group: 'security',
    label: 'App integrity mode',
    description:
      'off: nothing is checked. shadow: apps are checked and would-block calls are logged (never blocked). enforce: failed checks are refused on sensitive actions. Start with shadow.',
    type: 'enum',
    options: ['off', 'shadow', 'enforce'],
    scopes: ['global'],
    seed: { global: 'shadow' },
  },
  {
    key: 'integrity.min_device_verdict',
    group: 'security',
    label: 'Lowest device level accepted',
    description:
      'Play Integrity device level a phone must reach. Many low-cost phones never reach strong, so device is the usual choice.',
    type: 'enum',
    options: ['basic', 'device', 'strong'],
    scopes: ['global'],
    seed: { global: 'device' },
  },
  {
    key: 'integrity.allow_partner_web',
    group: 'security',
    label: 'Partner web dashboard skips the app check',
    description:
      'Restaurants accepting and marking orders ready from a browser (cookie sign-in) are not app-checked. Turn off to require the Partner app.',
    type: 'boolean',
    scopes: ['global'],
    seed: { global: true },
  },
  {
    key: 'scrape.max_per_10s',
    group: 'security',
    label: 'Browsing calls per 10 seconds',
    description: 'Menu, search and public reads per person or device in any 10 seconds.',
    type: 'integer',
    unit: 'calls',
    min: 5,
    max: 1000,
    scopes: ['global'],
    seed: { global: 40 },
  },
  {
    key: 'scrape.max_per_10min',
    group: 'security',
    label: 'Browsing calls per 10 minutes',
    description: 'Menu, search and public reads per person or device in any 10 minutes.',
    type: 'integer',
    unit: 'calls',
    min: 50,
    max: 20000,
    scopes: ['global'],
    seed: { global: 600 },
  },
  {
    key: 'scrape.ip_max_per_minute',
    group: 'security',
    label: 'Browsing calls per network per minute',
    description:
      'Backstop per IP address. Kept high because many phones share one mobile-network address (CGNAT).',
    type: 'integer',
    unit: 'calls',
    min: 60,
    max: 100000,
    scopes: ['global'],
    seed: { global: 500 },
  },
  {
    key: 'scrape.slow_lane_ms',
    group: 'security',
    label: 'Slow lane delay',
    description:
      'Extra wait added to every browsing call from a repeat offender for 10 minutes. Data is never altered.',
    type: 'integer',
    unit: 'ms',
    min: 0,
    max: 5000,
    scopes: ['global'],
    seed: { global: 1500 },
  },
  {
    key: 'scrape.strikes_for_slow_lane',
    group: 'security',
    label: 'Limit hits before the slow lane',
    description: 'How many times a key may hit a limit in 10 minutes before it is slowed down.',
    type: 'integer',
    unit: 'hits',
    min: 1,
    max: 50,
    scopes: ['global'],
    seed: { global: 3 },
  },
  /* Data-layer zero trust (W25). */
  {
    key: 'turnstile.fail_mode',
    group: 'security',
    label: 'When the human check is unreachable',
    description:
      'Public web forms (site messages, web sign-in codes, web area votes) are protected by Cloudflare Turnstile. open: if Cloudflare cannot be reached the form still goes through (logged). closed: the form is refused until Cloudflare answers.',
    type: 'enum',
    options: ['open', 'closed'],
    scopes: ['global'],
    seed: { global: 'open' },
  },
];

const CATALOGUE_BY_KEY = new Map(CONFIG_CATALOGUE.map((def) => [def.key, def]));

export function configDef(key: string): ConfigKeyDef | undefined {
  return CATALOGUE_BY_KEY.get(key);
}

/** The base scope every key must have a value at: global if allowed, otherwise each country. */
export function configBaseScope(def: Pick<ConfigKeyDef, 'scopes'>): 'global' | 'country' {
  return def.scopes.includes('global') ? 'global' : 'country';
}

/** Checks a value against its key definition. Returns an error message or null. */
export function configValueError(def: ConfigKeyDef, value: ConfigValue): string | null {
  const range = (n: number): string | null => {
    if (def.min !== undefined && n < def.min) return `Must be at least ${def.min}`;
    if (def.max !== undefined && n > def.max) return `Must be at most ${def.max}`;
    return null;
  };
  switch (def.type) {
    case 'boolean':
      return typeof value === 'boolean' ? null : 'Must be on or off';
    case 'enum':
      return typeof value === 'string' && (def.options ?? []).includes(value)
        ? null
        : 'Pick one of the listed options';
    case 'integer':
    case 'money':
    case 'time':
      if (typeof value !== 'number' || !Number.isInteger(value)) return 'Must be a whole number';
      return range(value);
    case 'decimal':
    case 'percent':
      if (typeof value !== 'number' || !Number.isFinite(value)) return 'Must be a number';
      if (Math.round(value * 100) !== value * 100) return 'Use at most 2 decimal places';
      return range(value);
    case 'text': {
      if (typeof value !== 'string') return 'Must be text';
      const length = value.trim().length;
      if (length < (def.min ?? 1)) return 'Enter a value';
      if (length > (def.max ?? 200)) return `Use at most ${def.max ?? 200} characters`;
      return null;
    }
  }
}

/* ------------------------------------------------------------------ */
/* API shapes                                                          */
/* ------------------------------------------------------------------ */

export const ConfigScopeSchema = z
  .object({ type: ConfigScopeTypeSchema, id: z.string().min(1).max(64).nullable() })
  .refine((s) => (s.type === 'global') === (s.id === null), {
    message: 'Global has no id; every other scope needs one',
  })
  .refine((s) => s.type !== 'country' || CountryCodeSchema.safeParse(s.id).success, {
    message: 'Country must be IN or NP',
  })
  .refine((s) => (s.type !== 'town' && s.type !== 'zone') || z.uuid().safeParse(s.id).success, {
    message: 'Town and zone scopes need an id',
  });
export type ConfigScope = z.infer<typeof ConfigScopeSchema>;

export const ConfigValueRowSchema = z.object({
  scope: z.object({ type: ConfigScopeTypeSchema, id: z.string().nullable() }),
  /** "All countries", "India", "Lucknow", "Lucknow · LKO-01" */
  scopeLabel: z.string(),
  /** Country of this row (null only for global), used to format money. */
  countryCode: CountryCodeSchema.nullable(),
  value: ConfigValueSchema,
  version: z.number().int(),
  note: z.string().nullable(),
  updatedBy: z.string(),
  updatedByName: z.string().nullable(),
  updatedAt: z.string(),
});
export type ConfigValueRow = z.infer<typeof ConfigValueRowSchema>;

export const ConfigEffectiveSchema = z.object({
  value: ConfigValueSchema,
  source: z.object({ type: ConfigScopeTypeSchema, id: z.string().nullable() }),
  sourceLabel: z.string(),
  countryCode: CountryCodeSchema.nullable(),
});
export type ConfigEffective = z.infer<typeof ConfigEffectiveSchema>;

export const ConfigKeyViewSchema = z.object({
  key: z.string(),
  group: ConfigGroupSchema,
  groupLabel: z.string(),
  label: z.string(),
  description: z.string(),
  type: ConfigValueTypeSchema,
  unit: z.string().nullable(),
  min: z.number().nullable(),
  max: z.number().nullable(),
  options: z.array(z.string()),
  scopes: z.array(ConfigScopeTypeSchema),
  values: z.array(ConfigValueRowSchema),
  /** Value in force for the scope asked about; null when no scope was given. */
  effective: ConfigEffectiveSchema.nullable(),
  overrideCount: z.number().int(),
  needsReview: z.boolean(),
});
export type ConfigKeyView = z.infer<typeof ConfigKeyViewSchema>;

export const ConfigHistoryEntrySchema = z.object({
  id: z.number().int(),
  key: z.string(),
  scope: z.object({ type: ConfigScopeTypeSchema, id: z.string().nullable() }),
  scopeLabel: z.string(),
  action: z.enum(['set', 'clear', 'seed']),
  oldValue: ConfigValueSchema.nullable(),
  newValue: ConfigValueSchema.nullable(),
  reason: z.string(),
  actor: z.string(),
  actorName: z.string().nullable(),
  at: z.string(),
});
export type ConfigHistoryEntry = z.infer<typeof ConfigHistoryEntrySchema>;

export const ConfigKeyDetailSchema = ConfigKeyViewSchema.extend({
  history: z.array(ConfigHistoryEntrySchema),
});
export type ConfigKeyDetail = z.infer<typeof ConfigKeyDetailSchema>;

/** Where to resolve values for: a country, a town or a zone (most specific wins). */
export const ConfigScopeQuerySchema = z.object({
  country: CountryCodeSchema.optional(),
  townId: z.uuid().optional(),
  zoneId: z.uuid().optional(),
});
export type ConfigScopeQuery = z.infer<typeof ConfigScopeQuerySchema>;

export const SetConfigValueInputSchema = z.object({
  scope: ConfigScopeSchema,
  value: ConfigValueSchema,
  /** null when creating a new override; the row version when changing one. */
  expectedVersion: z.number().int().min(1).nullable(),
  reason: z.string().trim().min(3, 'Say why (at least 3 characters)').max(200),
});
export type SetConfigValueInput = z.infer<typeof SetConfigValueInputSchema>;

export const ClearConfigValueInputSchema = z.object({
  scope: ConfigScopeSchema,
  expectedVersion: z.number().int().min(1),
  reason: z.string().trim().min(3, 'Say why (at least 3 characters)').max(200),
});
export type ClearConfigValueInput = z.infer<typeof ClearConfigValueInputSchema>;

export type ResolvedConfig = Record<string, ConfigEffective>;

/** Formats a money value (minor units) for a country. */
export function formatConfigMoney(minor: number, country: CountryCode | null): string {
  const major = minor / 100;
  const text = major.toLocaleString('en-IN', {
    minimumFractionDigits: Number.isInteger(major) ? 0 : 2,
    maximumFractionDigits: 2,
  });
  if (country === 'NP') return `Rs ${text}`;
  return `₹${text}`;
}
